import { Box, Skeleton } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { useCallback, useEffect } from "react";

import { FeatureFlagged } from "@/Components/FeatureFlagging";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { AnswerDebugDetail } from "./components/AnswerDebug";
import { AnswerFeedback } from "./components/AnswerFeedback";
import { AnswerMessages, ErrorAnswerMessages } from "./components/AnswerMessages";
import { AskQuestionInput } from "./components/AskQuestionInput";
import { ClearChatButton } from "./components/ClearChatButton";
import { Disclaimer } from "./components/Disclaimer";
import { EmptyChat } from "./components/EmptyChat";
import { PendingAnswerMessageBubble } from "./components/PendingAnswerMessageBubble";
import { ScenarioBuilderDisclaimer } from "./components/ScenarioBuilderDisclaimer";
import { StatusAlert } from "./components/StatusAlert";
import { UserQuestionChatBubble } from "./components/UserQuestionBubble";
import { useChat } from "./hooks/useChat";
import { useMetadata } from "./hooks/useMetadata";
import { ChatMessage } from "./types";

const PendingChat = () => (
  <Box flex={1} display="flex" flexDirection="column" alignSelf="stretch" p={3}>
    <Skeleton variant="rounded" height="3rem" width="100%" sx={{ borderRadius: 3, mb: 2 }} />

    <PendingAnswerMessageBubble />
  </Box>
);

type ChatProps = {
  prefillQuestion?: string | null;
};

export const Chat = ({ prefillQuestion }: ChatProps) => {
  const { chatMessages, askQuestion, pendingQuestion, isInitiallyLoadingMessages } = useChat();
  const { value: isMaiteChatHistoryEnabled } = useFeatureGate(featureFlag.maiteChatHistory);
  const { value: isWarcFeatureEnabled } = useFeatureGate(featureFlag.enableWarc);

  const {
    hendrenTier,
    monthlyQueryLimit,
    isLoading: isMetadataLoading,
    queryLimitReached,
    remainingQueries,
  } = useMetadata();

  // WARC is enabled if both the feature flag is on AND the user has premium tier
  const warcEnabledForTier = isWarcFeatureEnabled && hendrenTier === "PREMIUM";

  useEffect(() => {
    if (prefillQuestion && !isMetadataLoading && !queryLimitReached && !pendingQuestion) {
      askQuestion(prefillQuestion, warcEnabledForTier);
    }
  }, [prefillQuestion, askQuestion]);

  const ChatMessages = useCallback(
    ({ messages }: { messages: ChatMessage[] }) => {
      if (!pendingQuestion && messages.length === 0) {
        return <EmptyChat onAskQuestion={askQuestion} warcEnabledForTier={warcEnabledForTier} />;
      }

      return (
        <>
          {messages.map((message, index) =>
            message.user === "user" ? (
              <UserQuestionChatBubble key={index} question={message.content} mb={1.5} />
            ) : (
              <Box key={index} mb={message.isError ? 0 : 4}>
                {message.isError && message.error ? (
                  <ErrorAnswerMessages key={index} messages={message.error} />
                ) : (
                  <AnswerMessages
                    key={index}
                    messages={
                      message.isError
                        ? (message.error ?? "I can't help you with that.")
                        : message.content
                    }
                  />
                )}

                {Boolean(message.isScenariosQuery) && (
                  <FeatureFlagged featureName="hendrenUseScenariosTool">
                    <ScenarioBuilderDisclaimer />
                  </FeatureFlagged>
                )}
                {Boolean(message.debug) && (
                  <FeatureFlagged featureName="maiteAnswerDebug">
                    <AnswerDebugDetail debugValue={message.debug} />
                  </FeatureFlagged>
                )}
                {!message.isError && (
                  <FeatureFlagged featureName="maiteAnswerFeedback">
                    <AnswerFeedback messageId={message.id} score={message.feedbackScore} />
                  </FeatureFlagged>
                )}
              </Box>
            )
          )}
        </>
      );
    },
    [askQuestion, pendingQuestion, warcEnabledForTier]
  );

  if (isInitiallyLoadingMessages && isMetadataLoading) {
    return <PendingChat />;
  }

  return (
    <Box flex={1} display="flex" flexDirection="column" alignItems="stretch" p={3}>
      <Box flex={1} mb={3}>
        <ChatMessages messages={chatMessages} />

        {pendingQuestion && (
          <>
            <UserQuestionChatBubble question={pendingQuestion} mb={1.5} />
            <PendingAnswerMessageBubble mb={3} />
          </>
        )}

        {!isMaiteChatHistoryEnabled && chatMessages.length > 0 && !pendingQuestion && (
          <Box mb={2}>
            <ClearChatButton />
          </Box>
        )}
      </Box>

      <Box>
        {!queryLimitReached && hendrenTier === "FREE" && !isMetadataLoading && (
          <StatusAlert
            title={`You have ${remainingQueries} MAITE ${remainingQueries === 1 ? "query" : "queries"} remaining`}
            sx={{ mb: 1.5 }}
            severity="info"
          >
            GrowthOS users get {monthlyQueryLimit} free MAITE queries per month. Talk to your
            Marketing Science Partner to unlock full MAITE access. Get unlimited queries, chat
            history, and much more.
          </StatusAlert>
        )}

        {queryLimitReached && !isMetadataLoading && (
          <StatusAlert
            title="You've used all your free queries for this month"
            sx={{ mb: 1.5 }}
            severity="warning"
          >
            Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited
            queries, chat history, and much more.
          </StatusAlert>
        )}

        {!queryLimitReached && !isMetadataLoading && (
          <AskQuestionInput
            onAskQuestion={askQuestion}
            isAnswerPending={Boolean(pendingQuestion)}
            warcEnabledForTier={warcEnabledForTier}
            prefillQuestion={prefillQuestion || undefined}
            mb={1.5}
          />
        )}

        {isMetadataLoading && (
          <Skeleton variant="rounded" height="6rem" width="100%" sx={{ mb: 2 }} />
        )}

        <Disclaimer />
      </Box>
    </Box>
  );
};
